import { useCallback, useEffect, useLayoutEffect, useRef, useState } from 'react';
import { Table } from 'antd';
import type { TableProps } from 'antd';

/** Never squeeze the rows below this, however short the viewport gets. */
const MIN_BODY_HEIGHT = 120;

/** Below this the columns would be cramped, so scroll sideways instead. */
const DEFAULT_MIN_WIDTH = 1000;

/**
 * An antd Table measured against the space its parent gives it.
 *
 * Passing `scroll.y` is what makes antd split the header from the body, so the
 * column titles and the pager stay pinned and only the rows move. We measure
 * instead of hardcoding a `calc()` because the space left over differs per
 * screen — a page may carry a notice, a toolbar, both or neither.
 *
 * A fixed header also puts rc-table into `table-layout: fixed`, where column
 * widths come from the `width` a column declares and the rest share what is
 * left. `scroll.x` is therefore a *minimum*: the table stretches to fill a wide
 * panel and scrolls sideways on a narrow one instead of crushing the columns.
 */
type DataTableProps<T> = TableProps<T> & {
  /** Width below which the table scrolls horizontally rather than squeezing. */
  minWidth?: number;
};

export default function DataTable<T extends object>({
  scroll,
  size = 'middle',
  minWidth = DEFAULT_MIN_WIDTH,
  ...rest
}: DataTableProps<T>) {
  const wrapper = useRef<HTMLDivElement>(null);
  const [bodyHeight, setBodyHeight] = useState<number>();

  const measure = useCallback(() => {
    const el = wrapper.current;
    if (!el) return;

    // On the first pass `scroll.y` is unset, so there is no separate header
    // element yet and we over-estimate by its height; the resulting re-render
    // measures again with the real layout and settles.
    const header = el.querySelector<HTMLElement>('.ant-table-header');
    const pager = el.querySelector<HTMLElement>('.ant-table-pagination');
    const available =
      el.clientHeight - (header?.offsetHeight ?? 0) - (pager?.offsetHeight ?? 0);

    setBodyHeight((previous) => {
      const next = Math.max(Math.floor(available), MIN_BODY_HEIGHT);
      // Ignore sub-pixel noise, otherwise the observer and the layout effect
      // can keep nudging each other.
      return previous !== undefined && Math.abs(previous - next) < 2 ? previous : next;
    });
  }, []);

  // After every render: the pager appears, disappears or changes height as the
  // data does, and that never resizes the wrapper itself.
  useLayoutEffect(measure);

  // Window resizes, sider collapses, drawers — anything that changes the frame.
  useEffect(() => {
    const el = wrapper.current;
    if (!el) return;

    const observer = new ResizeObserver(measure);
    observer.observe(el);
    return () => observer.disconnect();
  }, [measure]);

  return (
    <div className="data-table" ref={wrapper}>
      <Table<T>
        size={size}
        scroll={{ x: minWidth, y: bodyHeight, ...scroll }}
        {...rest}
      />
    </div>
  );
}
